import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Calendar,
} from '@hero/web-components';
import { useOrderTimeSlots, type TimeSlot } from '@hero/hooks-shared';

export interface PickupDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { date: Date; timeSlot: string; dateRange: { from: Date; to: Date } }) => void;
}

/**
 * PickupDateModal - Reusable modal for selecting pickup date and time
 *
 * Manages all date/time selection logic using useOrderTimeSlots hook.
 * Used in both "Order Again" flow and /order/new page.
 *
 * @example
 * ```tsx
 * <PickupDateModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={({ date, timeSlot, dateRange }) => {
 *     console.log('Selected:', date, timeSlot, dateRange);
 *   }}
 * />
 * ```
 */
export const PickupDateModal: React.FC<PickupDateModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const { t, i18n } = useTranslation('m-order');
  const isRTL = i18n.language === 'ar';

  // ==================== Time Slots Hook ====================
  const {
    availableSlots,
    selectedSlot,
    selectedDate: hookSelectedDate,
    dateRange: updatedPickupDate,
    selectSlot,
    selectDate,
    isDateValid,
  } = useOrderTimeSlots();

  const selectedDate = hookSelectedDate.toDate();
  const isDateDisabled = (date: Date) => !isDateValid(date);

  // ==================== Committed State Management ====================
  // Track the last confirmed selection to restore on cancel
  const [committedDate, setCommittedDate] = useState<Date | null>(null);
  const [committedSlot, setCommittedSlot] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);

  // Initialize committed state from auto-selected values on first load
  // AND notify parent immediately so they can display the auto-selected date
  useEffect(() => {
    if (!hasInitializedRef.current && selectedDate && selectedSlot && updatedPickupDate) {
      hasInitializedRef.current = true;
      setCommittedDate(selectedDate);
      setCommittedSlot(selectedSlot);

      // Notify parent of auto-selected date immediately
      onConfirm({
        date: selectedDate,
        timeSlot: selectedSlot,
        dateRange: updatedPickupDate,
      });
    }
  }, [selectedDate, selectedSlot, updatedPickupDate, onConfirm]);

  // Reset to committed state when modal reopens
  useEffect(() => {
    if (isOpen && committedDate && committedSlot) {
      selectDate(committedDate);
      selectSlot(committedSlot);
    }
  }, [isOpen]);

  // ==================== Handlers ====================
  const handleConfirm = () => {
    if (!updatedPickupDate || !selectedSlot) return;

    // Save the confirmed selection
    setCommittedDate(selectedDate);
    setCommittedSlot(selectedSlot);

    onConfirm({
      date: selectedDate,
      timeSlot: selectedSlot,
      dateRange: updatedPickupDate,
    });

    onClose();
  };

  const handleClose = () => {
    // Reset to last committed selection on cancel
    if (committedDate && committedSlot) {
      selectDate(committedDate);
      selectSlot(committedSlot);
    }
    onClose();
  };

  const isConfirmDisabled = !selectedDate || !selectedSlot;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="p-8 bg-white">
        <DialogHeader>
          <DialogTitle
            className={`text-[18px] font-[700] leading-[28px] ${
              isRTL ? 'text-right' : 'text-left'
            }`}>
            {t('drawer.select_date_time')}
          </DialogTitle>
        </DialogHeader>

        <div className="w-full space-y-5 mt-4">
          {/* Date Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h4 className={`text-sm font-semibold text-gray-900 mb-4 ${isRTL ? 'text-right' : ''}`}>
              {t('drawer.select_pickup_time')}
            </h4>
            <div className="flex justify-center mb-5">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={date => {
                  if (date) {
                    selectDate(date);
                  }
                }}
                disabled={isDateDisabled}
                className="rounded-xl border border-gray-200 shadow-md"
              />
            </div>

            <Select
              value={selectedSlot || undefined}
              onValueChange={selectSlot}
              disabled={availableSlots.length === 0}>
              <SelectTrigger
                className={`w-full p-4 text-gray-900 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors ${
                  isRTL ? 'text-right' : ''
                }`}>
                <SelectValue
                  placeholder={
                    availableSlots.length === 0
                      ? t('drawer.no_available_slots')
                      : t('drawer.select_pickup_time')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableSlots.map((slot: TimeSlot) => (
                  <SelectItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Save Button */}
          <Button
            type="button"
            className="w-full mt-6 rounded-xl text-base leading-6 font-semibold py-6 shadow-md hover:shadow-lg transition-shadow"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}>
            {t('drawer.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
